import { UserManager, WebStorageStateStore } from 'oidc-client-ts'

/**
 * How long before expiry the manager raises `accessTokenExpiring`.
 *
 * The library default is 60s, which was a comfortable margin when the access
 * token lasted an hour. At a 10-minute lifetime the token turns over ~6x per
 * hour, so a single missed window is much likelier — hence the wider lead.
 *
 * Lives here rather than in renew-session because this is the value that
 * configures the manager, and importing it the other way would make the two
 * modules circular.
 */
export const SCHEDULED_RENEW_LEAD_SECONDS = 120

/**
 * The one UserManager for the lifetime of this window.
 *
 * It is held here rather than letting `<AuthProvider {...config}>` build it
 * internally for one reason: react-oidc-context wraps `signinSilent` (and every
 * other navigator method) in a handler that CATCHES the error, dispatches it to
 * context state and resolves with `null`. A caller therefore cannot tell an
 * expired refresh token (`invalid_grant`, session is over) from a network blip
 * (session is fine) — and treating the two alike is what turns a dropped packet
 * into a logout. Calling `signinSilent` on this instance surfaces the real
 * `ErrorResponse`, which `isDeadSessionError` needs to make that distinction.
 */
let manager: UserManager | null = null
let managerKey: string | null = null

export interface AuthManagerSettings {
  authority?: string
  clientId?: string
  /**
   * Preserved from the previous config as-is. This is a browser client, so the
   * value ships in the bundle — removing it is tracked separately and needs the
   * identity team to confirm whether this client is registered public or
   * confidential first, since login flows through the same setting.
   */
  clientSecret?: string
  redirectUri?: string
  uiLocales?: string
}

/**
 * Build the manager on first call and return that same instance afterwards.
 *
 * Lazy rather than module-level because the authority and client_id may arrive
 * from the Module Federation host at mount time: embedded, mmp points at the
 * host's OIDC client so it reads the SAME localStorage session key the host
 * wrote, instead of starting a second session.
 */
export function initAuthManager({
  authority,
  clientId,
  clientSecret,
  redirectUri,
  uiLocales,
}: AuthManagerSettings): UserManager {
  const key = `${authority}:${clientId}`

  if (manager) {
    if (managerKey !== key) {
      // Swapping OIDC identity mid-page would strand the tokens already issued
      // under the previous key, so keep the first one and make the mismatch loud.
      console.warn(
        `[mmp][auth] UserManager already initialised as "${managerKey}"; ignoring "${key}".`
      )
    }
    return manager
  }

  managerKey = key
  manager = new UserManager({
    authority: authority ?? '',
    client_id: clientId ?? '',
    client_secret: clientSecret,
    redirect_uri: redirectUri ?? '',
    scope: 'address email openid profile roles offline_access api-profile',
    ui_locales: uiLocales,
    loadUserInfo: true,
    // localStorage (not the library default sessionStorage) so the session
    // survives a reload and the login popup's opener can read the token.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // The app owns renewal — see session-renewal-bridge. The library's own
    // service calls signinSilent() directly on this manager, bypassing the
    // single-flight in renew-session, so leaving it on means the scheduled
    // renewal and a 401 recovery can rotate the same one-time-use refresh
    // token concurrently. The loser gets invalid_grant and Duende revokes the
    // whole token family. The expiring EVENT still fires with this off; only
    // the built-in subscriber is gone.
    automaticSilentRenew: false,
    accessTokenExpiringNotificationTimeInSeconds: SCHEDULED_RENEW_LEAD_SECONDS,
  })

  return manager
}

/** The initialised manager. Throws if called before the AuthProvider renders. */
export function getAuthManager(): UserManager {
  if (!manager) {
    throw new Error('[mmp][auth] UserManager used before initAuthManager()')
  }
  return manager
}
